/**
 * Drag sensors, shared — V10 §7.
 *
 * ## The bug
 *
 * A draggable row spreads dnd-kit's `listeners` on its container, and those
 * listeners include an `onKeyDown` activator. React events bubble, so a keydown
 * inside an `<input>` **nested in that row** reaches the activator, which sees
 * Space (or Enter, both are `keyboardCodes.start` by default), calls
 * `event.preventDefault()` and lifts the row.
 *
 * dnd-kit has a guard for this — it bails when `event.target` is not the
 * activator node — but the guard only applies when `setActivatorNodeRef` was
 * used to nominate a handle. Every draggable row in this codebase makes the
 * whole row the handle, so `activatorNode.current` is null, the guard is
 * skipped, and every editable descendant loses Space and Enter.
 *
 * In the to-do list that is the entire report: a two-word title cannot be
 * typed, and Enter starts a keyboard drag instead of saving.
 *
 * ## The fix
 *
 * A sensor that refuses keys originating in an editable element, and **one**
 * `useDragSensors` behind every `DndContext` in the product. §7 asks for a
 * structural guard rather than a per-page patch for a reason: the per-page
 * version of this is `onKeyDown={stop}` on each input, which is a fix that has
 * to be remembered every time somebody adds a field to a draggable row, and
 * was not remembered here.
 */

import { KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import type { Activators, KeyboardSensorOptions, KeyboardCoordinateGetter } from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';

/** Tags that accept typed text, plus anything inside a rich-text editor. */
const EDITABLE_SELECTOR = 'input, textarea, select, [contenteditable=""], [contenteditable="true"]';

/**
 * Whether a keystroke came from somewhere a person is typing.
 *
 * Checks the element **and its ancestors**: a keydown inside TipTap lands on
 * whatever node the caret is in, not on the `contenteditable` host.
 */
export function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  if (target instanceof HTMLElement && target.isContentEditable) return true;
  return Boolean(target.closest(EDITABLE_SELECTOR));
}

/**
 * `KeyboardSensor`, minus the keys that belong to whoever is typing.
 *
 * Returning `false` from an activator is dnd-kit's own "not for me" — the
 * event is left entirely alone, so no `preventDefault` reaches the input and
 * no drag begins.
 */
export class EditableAwareKeyboardSensor extends KeyboardSensor {
  static activators: Activators<KeyboardSensorOptions> = [
    {
      eventName: 'onKeyDown',
      handler: (event, options, context) => {
        if (isEditableTarget(event.target)) return false;
        const base = KeyboardSensor.activators[0];
        return base.handler(event, options, context);
      },
    },
  ];
}

export interface DragSensorOptions {
  /**
   * How far a pointer travels before a press becomes a drag. Boards use 5px so
   * a click still opens a card (V5-02 §2.2); lists that open nothing on click
   * can be tighter.
   */
  pointerDistance?: number;
  /** Board drags step by column; lists use the sortable default. */
  coordinateGetter?: KeyboardCoordinateGetter;
}

/** The sensors behind every `DndContext` in the product. */
export function useDragSensors(options: DragSensorOptions = {}) {
  const { pointerDistance = 5, coordinateGetter = sortableKeyboardCoordinates } = options;
  return useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: pointerDistance } }),
    useSensor(EditableAwareKeyboardSensor, { coordinateGetter }),
  );
}
